import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { EpicExportConfig } from '@entity/epic-export-config';

type EpicExportFormControls = {
  externalIdType: FormControl<number | null>;
  startId: FormControl<number | null>;
  blockedIdsRaw: FormControl<string>;
};

// Parse the comma/newline-separated blocked-id textarea into a list of ids, or null when any
// token is not a non-negative safe integer. Shared by the reactive validator and save().
const parseBlockedIds = (raw: string): number[] | null => {
  if (!raw) {
    return [];
  }
  const tokens = raw
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const result: number[] = [];
  for (const token of tokens) {
    // Only plain non-negative decimal integers. Reject hex/octal/binary/scientific/signed/decimal
    // tokens that Number() would otherwise silently coerce (e.g. "0x10" -> 16, "1e3" -> 1000).
    if (!/^\d+$/.test(token)) {
      return null;
    }
    const value = Number(token);
    // Reject values beyond Number.MAX_SAFE_INTEGER (the backend stores blocked IDs as long;
    // unsafe integers would lose precision).
    if (!Number.isSafeInteger(value)) {
      return null;
    }
    result.push(value);
  }
  return result;
};

// Mirrors the backend's count cap ([MaxLength] on EpicExportConfig.BlockedIds). Without it, an
// over-long paste passes the form, the dialog closes (discarding the input), and the POST comes
// back 400 with only a generic toast.
const MAX_BLOCKED_IDS = 10000;

// Reactive validator so Angular owns the error lifecycle: the message clears as soon as the
// field becomes valid and form.invalid (and thus the disabled Save button) reflects it.
const blockedIdsValidator = (control: AbstractControl<string>): ValidationErrors | null => {
  const ids = parseBlockedIds(control.value);
  if (ids === null) {
    return { blockedIdsFormat: true };
  }
  return ids.length > MAX_BLOCKED_IDS ? { blockedIdsTooMany: true } : null;
};

// The backend caps ExternalIdType at int.MaxValue ([Range(0, int.MaxValue)]); StartId is a long, but
// JS numbers represent integers exactly only up to Number.MAX_SAFE_INTEGER, so bound StartId on the
// client to stop a silently rounded value being POSTed — the same precision guard parseBlockedIds
// already applies to the blocked ids.
const MAX_EXTERNAL_ID_TYPE = 2147483647; // int.MaxValue

@Component({
  selector: 'app-mapping-catalog-epic-export-dialog',
  templateUrl: './mapping-catalog-epic-export-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormNumberInputComponent, FormTextInputComponent, ButtonModule, TranslatePipe],
})
export class MappingCatalogEpicExportDialogComponent {
  private dynamicDialogRef = inject(DynamicDialogRef);

  // Character cap for the blocked-ids textarea. The real limit is a COUNT (10,000 ids, enforced by
  // blockedIdsValidator and the backend [MaxLength]); this only needs to be large enough that
  // 10,000 long ids (~16 digits + separator each) are never silently truncated. ~170k worst case,
  // rounded up.
  protected readonly blockedIdsMaxChars = 200_000;

  form = new FormGroup<EpicExportFormControls>({
    externalIdType: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(MAX_EXTERNAL_ID_TYPE),
    ]),
    startId: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(Number.MAX_SAFE_INTEGER),
    ]),
    blockedIdsRaw: new FormControl<string>('', { nonNullable: true, validators: [blockedIdsValidator] }),
  });

  save = (): void => {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const config: EpicExportConfig = {
      externalIdType: this.form.controls.externalIdType.value!,
      startId: this.form.controls.startId.value!,
      blockedIds: parseBlockedIds(this.form.controls.blockedIdsRaw.value) ?? [],
    };
    this.dynamicDialogRef.close(config);
  };

  cancel = (): void => {
    this.dynamicDialogRef.close();
  };
}
